import type {ShopifySalesChannel} from '@/types/salesChannels/shopifySalesChannels';
import type {
  CreateSalesChannelRequest,
  ImportProductsRequest,
  PostDisableOrderLoadingResponse,
  PostEnableOrderLoadingResponse,
  PostRequestDelegationResponse,
  RenameSalesChannelRequest,
} from '@myparcel-frontend/shopify-ecommerce-service';
import {useShopifyEcommerceService} from '@myparcel-frontend/shopify-ecommerce-service';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import logToMonitor from '@/utils/logging';

export default function useShopifyEcommerceSalesChannel() {
  const salesChannelStore = useSalesChannelStore();

  const getShopifySalesChannels = async (shopIds: string[]): Promise<ShopifySalesChannel[] | []> => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.getSalesChannels(shopIds);
      const result: ShopifySalesChannel[] = [];

      if (!response || !response?.results?.length) {
        return result;
      }

      for (const salesChannel of response.results) {
        const shopifySalesChannel: ShopifySalesChannel = {
          ...salesChannel,
          ecommercePlatform: 'SHOPIFY',
        };

        const newSalesChannel = salesChannelStore.addSalesChannel(shopifySalesChannel);

        result.push(newSalesChannel);
      }

      return result;
    } catch (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching Shopify sales channels failed in useShopify composable',
      });

      throw error;
    }
  };

  const createShopifySalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.createSalesChannel(data);

      const newShopifySalesChannel: ShopifySalesChannel = {
        ...response,
        ecommercePlatform: 'SHOPIFY',
      };

      salesChannelStore.addSalesChannel(newShopifySalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating Shopify sales channel failed in useShopify composable',
        _myparcel_sales_channel_payload: data,
      });

      return undefined;
    }
  };

  const removeShopifySalesChannel = async (salesChannelId: string) => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.removeSalesChannel(salesChannelId);

      salesChannelStore.removeSalesChannel(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing Shopify sales channel failed in useShopify composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest) => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.renameSalesChannel(data);

      const updatedShopifySalesChannel: ShopifySalesChannel = {
        ...response,
        ecommercePlatform: 'SHOPIFY',
      };
      salesChannelStore.updateSalesChannel(updatedShopifySalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Renaming Shopify sales channel failed in useShopify composable',
        _myparcel_sales_channel: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }
  };

  const enableOrderLoading = async (salesChannelId: string): Promise<PostEnableOrderLoadingResponse | undefined> => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.enableOrderLoading(salesChannelId);

      salesChannelStore.enableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for Shopify sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const disableOrderLoading = async (salesChannelId: string): Promise<PostDisableOrderLoadingResponse | undefined> => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.disableOrderLoading(salesChannelId);

      salesChannelStore.disableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for Shopify sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse | undefined> => {
    const client = useShopifyEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    if (response) {
      salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
        authorizeUrl: response.authorizeUrl,
      });
    }

    return response;
  };

  const fetchShopifySalesChannelById = async (salesChannelId: string): Promise<ShopifySalesChannel | undefined> => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.getSalesChannelById(salesChannelId);

      const shopifySalesChannel: ShopifySalesChannel = {
        ...response,
        ecommercePlatform: 'SHOPIFY',
      };

      salesChannelStore.addSalesChannel(shopifySalesChannel);

      return shopifySalesChannel;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Fetching Shopify sales channel by id failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const importShopifyProducts = async (saleschannelIds: ImportProductsRequest['salesChannelIds']) => {
    const client = useShopifyEcommerceService();

    try {
      const response = await client.importProducts(saleschannelIds);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Importing Shopify products failed in useShopify composable',
        _myparcel_sales_channel_ids: saleschannelIds,
      });
      throw error;
    }
  };

  return {
    getShopifySalesChannels,
    createShopifySalesChannel,
    removeShopifySalesChannel,
    renameSalesChannel,
    enableOrderLoading,
    disableOrderLoading,
    requestDelegation,
    fetchShopifySalesChannelById,
    importShopifyProducts,
  };
}
